import { mainApi } from "services/main";
import { GaiaConstants } from "utils/gaia-constants";
import { Document } from "models/schemas";
import { isGaiaServiceIdentity } from "utils/validation";

export const withUserAttribution = (doc: Document): Document =>
  isGaiaServiceIdentity(doc.modifiedBy)
    ? { ...doc, modifiedBy: doc.createdBy, modifiedAt: doc.createdAt }
    : doc;

export const getFileNameWithExtension = (doc: any): string => {
  const extension = doc.extensionType || "";
  if (
    extension === GaiaConstants.FOLDER_EXTENSION_TYPE ||
    extension === GaiaConstants.LINK_EXTENSION_TYPE
  ) {
    return doc.fileName;
  }
  return `${doc.fileName}${extension}`;
};

// Convert parentId to readable path for search
export const getReadablePath = (parentId: string, teamName: string): string => {
  if (!parentId || parentId === teamName) return "";

  const relativePath = parentId
    .replace(teamName, "")
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean)
    .map((part) => part.trim());
  return relativePath.join(" > ");
};

export const getRelatedToLink = (
  parentId: string,
  category: string,
  gaiaObjectId: string,
): string | null => {
  if (parentId.startsWith("TSK")) {
    return `/${getObjectTypeFromId(parentId)}/${parentId}`;
  }
  if (parentId.startsWith("E")) {
    return `/events/${parentId}`;
  }
  if (getObjectTypeFromId(parentId).length !== 0) {
    return `/${
      category === "audits" ? "audit" : category
    }/${gaiaObjectId}/${getObjectTypeFromId(parentId)}/${parentId}`;
  }
  return null; // fallback for unknown types
};

export const getObjectTypeFromId = (id: string): string => {
  if (id.startsWith("TSK")) {
    return "Task";
  }
  if (id.startsWith("payment")) {
    return "Payment";
  }
  if (id.startsWith("assessment")) {
    return "Assessment";
  }
  if (id.startsWith("E")) {
    return "Event";
  }
  return ""; // fallback for unknown types
};

/**
 * Get delete info message for Document items
 */
export const deleteInfoNew = (docs: Document[], suffix: string): string => {
  if (docs.length === 0) {
    return "";
  }
  const linkNum = docs.filter((d) => isLink(d)).length;
  const folderNum = docs.filter((d) => d.isFolder).length;
  const fileNum = docs.filter((d) => !isLink(d) && !d.isFolder).length;

  const parts: string[] = [];
  if (folderNum > 0) {
    parts.push(`${folderNum === 1 ? "folder" : "folders"}`);
  }
  if (linkNum > 0) {
    parts.push(`${linkNum === 1 ? "link" : "links"}`);
  }
  if (fileNum > 0) {
    parts.push(`${fileNum === 1 ? "file" : "files"}`);
  }

  let joinedParts: string;
  if (parts.length === 1) {
    joinedParts = parts[0];
  } else if (parts.length === 2) {
    joinedParts = parts.join(" and ");
  } else {
    joinedParts = `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  }

  return `Delete ${joinedParts}${suffix}`;
};

/**
 * Get delete message for Document items
 */
export const getDeleteMessageNew = (
  docs: Document[],
  fileNames: string,
): React.ReactElement => {
  const hasVersions = docs.some(
    (doc) => doc.versions && doc.versions.length > 0,
  );

  if (docs.length === 1) {
    const doc = docs[0];
    const displayName = getFileNameWithExtension(doc);

    if (doc.isFolder) {
      return (
        <>
          Permanently delete folder <b>{displayName}</b> and all its contents?
        </>
      );
    }

    if (hasVersions) {
      return (
        <>
          Permanently delete <b>all versions</b> of <b>{displayName}</b>?
        </>
      );
    }

    return (
      <>
        Permanently delete <b>{displayName}</b>?
      </>
    );
  }

  // Multiple items
  if (hasVersions) {
    return (
      <>
        Permanently delete <b>{fileNames}</b> including <b>all versions</b>?
      </>
    );
  }

  return (
    <>
      Permanently delete <b>{fileNames}</b>?
    </>
  );
};

/**
 * Get file names as comma-separated string
 */
export const getFileNamesNew = (docs: Document[]): string => {
  return docs.map((doc) => getFileNameWithExtension(doc)).join(", ");
};

/**
 * Check if document is a link (external URL)
 */
export const isLink = (doc: Document): boolean => {
  return doc.extensionType === GaiaConstants.LINK_EXTENSION_TYPE;
};

/** A file imported from SharePoint: metadata only, no bytes in Gaia. */
export const isSharePointPointer = (doc: Document): boolean =>
  Boolean(doc.sharePointSource);

/**
 * Invalidates the "Documents" cache tag after OpenSearch sync delay.
 * Use in onQueryStarted handlers for mutations that modify documents.
 */
export const invalidateDocumentsCache = async (
  dispatch: (action: unknown) => void,
  queryFulfilled: Promise<unknown>,
) => {
  await queryFulfilled;
  setTimeout(() => {
    dispatch(mainApi.util.invalidateTags(["Documents"]));
  }, GaiaConstants.OPENSEARCH_SYNC_DELAY);
};
